import CommonModal from "@/components/common/CommonModal";
import I18Translation from "@/components/I18Translation";
import ResetPassword from "@/components/ResetPassword";
import ThemeSwitcher from "@/components/ThemeSwitcher";
import { useUser } from "@/context/useUser";
import { getTenantId, getUserInfo } from "@/lib/storeManager";
import { trimPath } from "@/lib/trimPath";
import { MenuService } from "@/services/APIs";
import navigationRoutes from "@/utils/navigationRoutes";
import { IResetPasswordRefType } from "@/utils/types/resetPasswordRef";
import {
  CaretDownFilled,
  EyeInvisibleOutlined,
  LogoutOutlined,
} from "@ant-design/icons";
import { ErrorBoundary, ProLayout } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import { Dropdown, Spin } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";

const CustomHeaderTitle = (logo: React.ReactNode, title: React.ReactNode) => {
  return (
    <Link
      to={navigationRoutes.SCHEMAS}
      className="flex items-center gap-2 no-underline"
    >
      {logo}
      <span className="text-lg font-bold text-primary">{title}</span>
      <CaretDownFilled className="text-xs" />
    </Link>
  );
};

const CustomAvatar = () => {
  const { resetPassword } = useUser();
  const { isAuthenticated, logout } = useUser();
  const [userInfo, setUserInfo] = useState<any>(null);

  const [open, setOpen] = useState(false);
  const resetPasswordRef = useRef<IResetPasswordRefType | null>(null);

  useEffect(() => {
    const userInfo = getUserInfo();
    setUserInfo(userInfo);
  }, [isAuthenticated]);

  const handleLogout = async () => {
    await logout();
  };

  const handleResetPassword = async () => {
    if (!resetPasswordRef.current) {
      return;
    }

    const values = resetPasswordRef.current.getFieldsValue();
    const response = await resetPassword(values);
    if (response) {
      return response;
    }
  };

  const CustomResetPassword = () => {
    return (
      <CommonModal
        title="Reset Password"
        open={open}
        onOk={() => {
          handleResetPassword(); // ✅ Call handler that uses ref
          setOpen(false);
        }}
        onCancel={() => {
          setOpen(false);
          resetPasswordRef.current?.resetFields(); // ✅ Optional: reset form on close
        }}
        width={800}
        okText="Reset Password"
        cancelText="Cancel"
        confirmLoading={false}
        destroyOnClose
      >
        <ResetPassword ref={resetPasswordRef} />
      </CommonModal>
    );
  };

  return (
    <div className="flex items-center gap-2">
      <Dropdown
        menu={{
          items: [
            {
              key: "theme",
              icon: <ThemeSwitcher />,
            },
            {
              key: "language",
              icon: <I18Translation />,
            },
            {
              key: "resetPassword",
              icon: <EyeInvisibleOutlined />,
              label: "Reset Password",
              onClick: () => setOpen(true),
              disabled: false,
            },
            {
              key: "logout",
              icon: <LogoutOutlined />,
              label: "Logout",
              onClick: handleLogout,
              danger: true,
            },
          ],
        }}
      >
        <div className="flex cursor-pointer items-center gap-2">
          <img
            src="https://i.pravatar.cc/32"
            alt="avatar"
            className="h-8 w-8 rounded-full"
          />
          <span className="text-sm font-medium">
            {userInfo?.first_name} {userInfo?.last_name}
          </span>
        </div>
      </Dropdown>
      <CustomResetPassword />
    </div>
  );
};

const MainLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const tenantId = getTenantId() ?? "";
  const { data: menuData, isLoading } = useQuery({
    queryKey: ["menus"],
    queryFn: () => MenuService.listMenus(),
  });

  const [settings] = useState({
    layout: "mix" as const,
    fixSiderbar: true,
    splitMenus: false,
  });

  const menuItems = useMemo(() => {
    if (!menuData || !Array.isArray(menuData)) {
      return [];
    }
    const map = (items: any[]): any[] =>
      items.map(item => ({
        path: item.path,
        name: item.title,
        key: item.id,
        // icon: item.icon_url ? (
        //   <img
        //     src={item.icon_url}
        //     alt="icon"
        //     style={{ width: 16, height: 16 }}
        //   />
        // ) : undefined,
        children: item.children?.length
          ? item.children.map((child: any) => ({
              path: trimPath(child.path),
              name: child.title,
              key: child.id,
              // icon: child.icon_url ? (
              //   <img
              //     src={child.icon_url}
              //     alt="icon"
              //     style={{ width: 16, height: 16 }}
              //   />
              // ) : undefined,
            }))
          : undefined,
      }));

    return map(menuData);
  }, [menuData]);

  if (isLoading) {
    return <Spin fullscreen />;
  }

  return (
    <ProLayout
      logo="https://cdn.textstudio.com/output/sample/normal/5/0/3/6/temporary-logo-860-16305.png"
      title={tenantId}
      route={{ path: "/", routes: menuItems ?? [] }}
      location={location}
      navTheme="light"
      fixedHeader
      menuItemRender={(item, dom) => (
        <div onClick={() => navigate(item.path!)}>{dom}</div>
      )}
      headerTitleRender={(logo, title) => CustomHeaderTitle(logo, title)}
      avatarProps={{
        src: "https://i.pravatar.cc/32",
        size: "small",
        title: "User",
        render: () => <CustomAvatar />,
      }}
      menuFooterRender={() => (
        <div className="text-center text-sm text-gray-400">
          © {new Date().getFullYear()} Universal Software
        </div>
      )}
      {...settings}
    >
      <ErrorBoundary>
        <Outlet />
      </ErrorBoundary>
    </ProLayout>
  );
};

export default MainLayout;
